import {
  Controller,
  ControllerProps,
  FieldValues,
  Path,
} from "react-hook-form";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

export type Option<T = any> = {
  label: string;
  value: T;
};

export type FormAutocompleteProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> = {
  label?: string;
  autocompleteProps?: Partial<AutocompleteProps<Option, false, false, false>>;
  textFieldProps?: TextFieldProps;
  controllerProps: Omit<ControllerProps<TFieldValues, TName>, "render">;
};

const FormAutocomplete = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  label,
  autocompleteProps = {},
  textFieldProps = {},
  controllerProps,
}: FormAutocompleteProps<TFieldValues, TName>) => {
  const {
    options = [],
    loading = false,
    disabled = false,
    noOptionsText = "No results found",
    onInputChange,
    ...rest
  } = autocompleteProps;

    const isRequired = !!controllerProps.rules?.required;

  return (
    <Controller<TFieldValues, TName>
      {...controllerProps}
      rules={controllerProps.rules}
      render={({ field, fieldState }) => (
        <Autocomplete<Option, false, false, false>
          {...rest}
          options={options}
          loading={loading}
          disabled={disabled}
          noOptionsText={noOptionsText}
          value={field.value || null}
          onChange={(_e, newVal) => field.onChange(newVal)}
          onInputChange={onInputChange}
          isOptionEqualToValue={(option, val) => option.value === val?.value}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={field.ref}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
              placeholder={textFieldProps.placeholder}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              {...textFieldProps}
              label={`${textFieldProps.label}${isRequired ? " *" : ""}`}
            />
          )}
        />
      )}
    />
  );
};

export default FormAutocomplete;
